import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ChevronLeft, Plus, Trash2, Save, Send, Eye } from 'lucide-react';
import api from '../../../services/api';
import LoadingSpinner from '../../common/LoadingSpinner';
import { toast } from 'react-toastify';
import { openOfferPdf } from './OfferLetterList';

const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500';

const EMPTY_FORM = {
  salutation: 'Ms',
  candidateName: '',
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  email: '',
  position: '',
  reportingManager: '',
  grade: '',
  postingId: '',
  dateOfJoining: '',
  jobResponsibilities: [''],
  monthlyFixedSalary: '',
  semiAnnualBonus: '',
  stockLakhs: '',
  probationMonths: 3,
};

function computeCtcPreview(monthly, semiAnnual, stockLakhs) {
  const m = Number(monthly) || 0;
  const s = Number(semiAnnual) || 0;
  const l = Number(stockLakhs) || 0;
  const annualFixed = m * 12;
  const annualBonus = s * 2;
  const annualStocks = l * 100000;
  const total = annualFixed + annualBonus + annualStocks;
  return {
    annualFixed,
    annualBonus,
    annualStocks,
    total,
    lpa: total / 100000,
  };
}

function firstName(full) {
  const p = String(full || '').trim().split(/\s+/);
  return p[0] || '';
}

const OfferLetterForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [letterStatus, setLetterStatus] = useState('draft');

  const loadMeta = useCallback(async () => {
    try {
      const res = await api.get('/admin/offer-letters/meta');
      setMeta(res.data);
    } catch (e) {
      toast.error('Failed to load form options');
    }
  }, []);

  const loadLetter = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/offer-letters/${id}`);
      const l = res.data?.offerLetter;
      if (!l) throw new Error('Not found');
      if (!['draft', 'rejected'].includes(l.status)) {
        toast.info('This letter cannot be edited');
        navigate('/admin/offer-letters');
        return;
      }
      setLetterStatus(l.status);
      setForm({
        salutation: l.salutation || 'Ms',
        candidateName: l.candidateName || '',
        addressLine1: l.addressLine1 || '',
        addressLine2: l.addressLine2 || '',
        addressLine3: l.addressLine3 || '',
        email: l.email || '',
        position: l.position || '',
        reportingManager: l.reportingManager || '',
        grade: l.grade || '',
        postingId: l.postingId || '',
        dateOfJoining: l.dateOfJoining
          ? new Date(l.dateOfJoining).toISOString().slice(0, 10)
          : '',
        jobResponsibilities:
          l.jobResponsibilities?.length ? l.jobResponsibilities : [''],
        monthlyFixedSalary: l.monthlyFixedSalary ?? '',
        semiAnnualBonus:
          l.semiAnnualBonus != null && Number(l.semiAnnualBonus) > 0
            ? l.semiAnnualBonus
            : '',
        stockLakhs: l.stockLakhs ?? '',
        probationMonths: l.probationMonths ?? 3,
      });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load offer letter');
      navigate('/admin/offer-letters');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, navigate]);

  useEffect(() => {
    loadMeta();
    loadLetter();
  }, [loadMeta, loadLetter]);

  const postingText = useMemo(() => {
    if (!meta?.postings || !form.postingId) return '';
    const p = meta.postings.find((x) => x.id === form.postingId);
    return p?.letterText || '';
  }, [meta, form.postingId]);

  const ctcPreview = useMemo(
    () => computeCtcPreview(form.monthlyFixedSalary, form.semiAnnualBonus, form.stockLakhs),
    [form.monthlyFixedSalary, form.semiAnnualBonus, form.stockLakhs],
  );

  const dearPreview = `Dear ${form.salutation} ${firstName(form.candidateName)},`;

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handlePositionChange = (value) => {
    setField('position', value);
  };

  const handleGradeChange = (gradeId) => {
    const g = meta?.grades?.find((x) => x.id === gradeId);
    setForm((prev) => ({
      ...prev,
      grade: gradeId,
      stockLakhs: g?.stockLakhs != null ? g.stockLakhs : prev.stockLakhs,
    }));
  };

  const addResponsibility = () => {
    setForm((prev) => ({
      ...prev,
      jobResponsibilities: [...prev.jobResponsibilities, ''],
    }));
  };

  const updateResponsibility = (idx, value) => {
    setForm((prev) => {
      const list = [...prev.jobResponsibilities];
      list[idx] = value;
      return { ...prev, jobResponsibilities: list };
    });
  };

  const removeResponsibility = (idx) => {
    setForm((prev) => {
      const list = prev.jobResponsibilities.filter((_, i) => i !== idx);
      return { ...prev, jobResponsibilities: list.length ? list : [''] };
    });
  };

  const buildPayload = (status) => {
    const responsibilities = form.jobResponsibilities
      .map((r) => String(r).trim())
      .filter(Boolean);
    return {
      status,
      salutation: form.salutation,
      candidateName: form.candidateName.trim(),
      addressLine1: form.addressLine1.trim(),
      addressLine2: form.addressLine2.trim(),
      addressLine3: form.addressLine3.trim(),
      email: form.email.trim(),
      position: form.position.trim(),
      reportingManager: form.reportingManager.trim(),
      grade: form.grade,
      postingId: form.postingId,
      dateOfJoining: form.dateOfJoining,
      jobResponsibilities: responsibilities,
      monthlyFixedSalary: Number(form.monthlyFixedSalary),
      semiAnnualBonus:
        form.semiAnnualBonus === '' || form.semiAnnualBonus == null
          ? null
          : Number(form.semiAnnualBonus),
      stockLakhs: Number(form.stockLakhs),
      probationMonths: Number(form.probationMonths) || 3,
    };
  };

  const validateClient = () => {
    const responsibilities = form.jobResponsibilities.filter((r) => String(r).trim());
    if (!form.candidateName.trim()) return 'Candidate name is required';
    if (!form.addressLine1.trim()) return 'Address line 1 is required';
    if (!form.email.trim()) return 'Email is required';
    if (!form.position.trim()) return 'Job title is required';
    if (!form.reportingManager.trim()) return 'Reporting manager is required';
    if (!form.grade) return 'Grade is required';
    if (!form.postingId) return 'Posting is required';
    if (!form.dateOfJoining) return 'Date of joining is required';
    if (responsibilities.length === 0) return 'Add at least one job responsibility';
    if (!form.monthlyFixedSalary || Number(form.monthlyFixedSalary) <= 0) {
      return 'Monthly fixed salary is required';
    }
    if (form.semiAnnualBonus !== '' && Number(form.semiAnnualBonus) < 0) {
      return 'Semi-annual bonus cannot be negative';
    }
    if (form.stockLakhs === '' || Number(form.stockLakhs) < 0) {
      return 'Stock value is required';
    }
    return null;
  };

  const save = async (status = 'draft') => {
    const err = validateClient();
    if (err) {
      toast.error(err);
      return null;
    }
    setSaving(true);
    try {
      const payload = buildPayload(status);
      let letterId = id;
      if (isNew) {
        const res = await api.post('/admin/offer-letters', payload);
        letterId = res.data?.offerLetter?._id;
        toast.success(res.data?.message || 'Saved');
        if (letterId) navigate(`/admin/offer-letters/${letterId}`, { replace: true });
      } else {
        const res = await api.put(`/admin/offer-letters/${id}`, payload);
        letterId = res.data?.offerLetter?._id || id;
        toast.success(res.data?.message || 'Updated');
      }
      return letterId;
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = () => save('draft');

  const handleSaveAndPreview = async () => {
    const letterId = await save('draft');
    if (letterId) {
      try {
        await openOfferPdf(letterId, true);
      } catch (e) {
        toast.error(e.message);
      }
    }
  };

  const handleSubmit = async () => {
    const err = validateClient();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post('/admin/offer-letters', buildPayload('pending'));
        toast.success('Submitted for Super Admin review');
        navigate('/admin/offer-letters');
      } else {
        await api.put(`/admin/offer-letters/${id}`, buildPayload('draft'));
        await api.put(`/admin/offer-letters/${id}/submit`);
        toast.success('Submitted for Super Admin review');
        navigate('/admin/offer-letters');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Submit failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" className="py-16" />;
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to="/admin/offer-letters"
          className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"
        >
          <ChevronLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            {isNew ? 'New offer letter' : 'Edit offer letter'}
          </h1>
          {!isNew && letterStatus === 'rejected' && (
            <p className="text-sm text-red-600">Previously rejected — edit and resubmit.</p>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-8">
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Candidate</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Salutation *</label>
              <select
                value={form.salutation}
                onChange={(e) => setField('salutation', e.target.value)}
                className={inputCls}
              >
                {(meta?.defaults?.salutations || ['Mr', 'Ms']).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Full name *</label>
              <input
                type="text"
                value={form.candidateName}
                onChange={(e) => setField('candidateName', e.target.value)}
                className={inputCls}
              />
            </div>
            <div className="sm:col-span-2">
              <p className="text-xs text-slate-500">Preview: <span className="font-medium text-slate-700">{dearPreview}</span></p>
            </div>
            <div className="sm:col-span-2">
              <p className="mb-3 text-xs text-slate-500">
                Enter three lines exactly as they should appear on the letter (line 1 is prefixed with &quot;Address:&quot; in the PDF).
              </p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Address line 1 *</label>
              <input
                type="text"
                value={form.addressLine1}
                onChange={(e) => setField('addressLine1', e.target.value)}
                className={inputCls}
                placeholder="e.g. 3 Number Main Road West,"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Address line 2</label>
              <input
                type="text"
                value={form.addressLine2}
                onChange={(e) => setField('addressLine2', e.target.value)}
                className={inputCls}
                placeholder="e.g. New Barrackpore, Shankar Pukur"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Address line 3</label>
              <input
                type="text"
                value={form.addressLine3}
                onChange={(e) => setField('addressLine3', e.target.value)}
                className={inputCls}
                placeholder="e.g. Kolkata, 700131"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Email *</label>
              <input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} className={inputCls} />
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Role</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Job title *</label>
              <input
                type="text"
                value={form.position}
                onChange={(e) => handlePositionChange(e.target.value)}
                className={inputCls}
                placeholder="e.g. HR Manager"
              />
              
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Reporting manager *</label>
              <input
                type="text"
                value={form.reportingManager}
                onChange={(e) => setField('reportingManager', e.target.value)}
                className={inputCls}
                placeholder="Mr. Prodyut Purkait, Director – Magpie Barter Private Limited"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Grade *</label>
              <select
                value={form.grade}
                onChange={(e) => handleGradeChange(e.target.value)}
                className={inputCls}
              >
                <option value="">Select grade</option>
                {(meta?.grades || []).map((g) => (
                  <option key={g.id} value={g.id}>{g.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Date of joining *</label>
              <input
                type="date"
                value={form.dateOfJoining}
                onChange={(e) => setField('dateOfJoining', e.target.value)}
                className={inputCls}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-700">Place of posting *</label>
              <select
                value={form.postingId}
                onChange={(e) => setField('postingId', e.target.value)}
                className={inputCls}
              >
                <option value="">Select posting</option>
                {(meta?.postings || []).map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
              {postingText && (
                <p className="mt-2 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{postingText}</p>
              )}
            </div>
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Job responsibilities</h2>
            <button
              type="button"
              onClick={addResponsibility}
              className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800"
            >
              <Plus className="mr-1 h-4 w-4" /> Add
            </button>
          </div>
          <div className="mt-4 space-y-3">
            {form.jobResponsibilities.map((row, idx) => (
              <div key={idx} className="flex gap-2">
                <span className="pt-2 text-sm font-medium text-slate-400 w-6">{idx + 1}.</span>
                <input
                  type="text"
                  value={row}
                  onChange={(e) => updateResponsibility(idx, e.target.value)}
                  className={inputCls}
                  placeholder="Responsibility description"
                />
                <button
                  type="button"
                  onClick={() => removeResponsibility(idx)}
                  className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                  title="Remove"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Compensation</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Fixed salary (monthly) *</label>
              <input
                type="number"
                min="0"
                value={form.monthlyFixedSalary}
                onChange={(e) => setField('monthlyFixedSalary', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Semi-annual performance bonus
              </label>
              <input
                type="number"
                min="0"
                value={form.semiAnnualBonus}
                onChange={(e) => setField('semiAnnualBonus', e.target.value)}
                className={inputCls}
                placeholder="Optional — leave blank if not applicable"
              />
              <p className="mt-1 text-xs text-slate-500">
                Optional. If left empty, this line is omitted from the offer letter.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Stock (lakhs) *</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={form.stockLakhs}
                onChange={(e) => setField('stockLakhs', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Probation (months)</label>
              <input
                type="number"
                min="1"
                max="24"
                value={form.probationMonths}
                onChange={(e) => setField('probationMonths', e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
          <div className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50/50 p-4 text-sm text-slate-700">
            <p className="font-semibold text-slate-900">CTC breakdown (preview)</p>
            <ul className="mt-2 space-y-1 text-slate-600">
              <li>Annual fixed: Rs {ctcPreview.annualFixed.toLocaleString('en-IN')}</li>
              <li>Annual bonus (2× semi-annual): Rs {ctcPreview.annualBonus.toLocaleString('en-IN')}</li>
              <li>Stocks: Rs {ctcPreview.annualStocks.toLocaleString('en-IN')}</li>
              <li className="font-semibold text-indigo-800">
                Total CTC: Rs {ctcPreview.lpa.toFixed(2)} LPA
              </li>
            </ul>
          </div>
        </section>

        <div className="flex flex-wrap gap-3 border-t border-slate-100 pt-6">
          <button
            type="button"
            disabled={saving}
            onClick={handleSaveDraft}
            className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <Save className="mr-1.5 h-4 w-4" />
            Save draft
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={handleSaveAndPreview}
            className="inline-flex items-center rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
          >
            <Eye className="mr-1.5 h-4 w-4" />
            Save & preview PDF
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={handleSubmit}
            className="inline-flex items-center rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            <Send className="mr-1.5 h-4 w-4" />
            Submit for review
          </button>
        </div>
      </div>
    </div>
  );
};

export default OfferLetterForm;
